/** SUNTRONIX — universal "✨ Fetch OEM Specifications" dialog.
 *  Resolves the OEM connector by Brand, fetches a technical-spec preview
 *  server-side, and lets the admin approve import into Product Master.
 *  Technical truth only — commercial data stays SUNTRONIX-owned. */
import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Sparkles, Search, CheckCircle2, ExternalLink, FileText, ChevronDown, Loader2, AlertTriangle,
} from "lucide-react";

const renderBold = (text) =>
  (text || "").split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={i} className="text-slate-900">{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>);

const STATUS_MSG = {
  NOT_FOUND: "Exact product not found — verify the part number / MTM.",
  REVIEW: "A similar model was returned — manual confirmation required (no substitution).",
  NO_SOURCE: "Connector configured but no live source yet — enter specs manually.",
  NO_CONNECTOR: "No OEM connector for this brand — enter specs manually.",
  SOURCE_UNAVAILABLE: "OEM source temporarily unavailable — existing data is retained.",
};

export default function OemSpecFetch({ brand, partNumber, onImport, onClose }) {
  const [part, setPart] = useState(partNumber || "");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [importing, setImporting] = useState(false);

  const search = async () => {
    if (!part.trim()) { toast.error("Enter the manufacturer part number / MTM"); return; }
    setBusy(true); setRes(null); setShowAll(false);
    try {
      const { data } = await api.post("/oem/fetch", { brand, part_number: part.trim() });
      setRes(data);
      if (data.match_status === "EXACT") toast.success(`Exact match: ${data.product_name}`);
      else toast.info(STATUS_MSG[data.match_status] || data.message || "No exact match");
    } catch (e) { toast.error(e.response?.data?.detail || "OEM fetch failed"); }
    setBusy(false);
  };

  const doImport = async () => {
    setImporting(true);
    try {
      // Populate the parent product form immediately (specs + smart description).
      onImport && onImport(res);
      toast.success("Specifications imported into the form — review & save");
      onClose();
    } catch (e) { toast.error("Import failed"); }
    setImporting(false);
  };

  const exact = res && res.match_status === "EXACT";
  const n = (exact && res.normalised) || {};

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col p-0" data-testid="oem-fetch-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
          <DialogTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-indigo-600" /> Fetch Product Intelligence
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            SUNTRONIX finds the official manufacturer specifications automatically from the Brand.
            Technical truth from the manufacturer — commercial data stays SUNTRONIX-owned.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 min-h-0 px-5 py-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-2 items-end">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-400">Brand</div>
              <div className="h-9 flex items-center px-2 rounded-md border border-slate-200 bg-slate-50 text-[13px] font-medium" data-testid="oem-brand">
                {brand || "—"}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-400">Manufacturer Part Number / MTM / SKU</div>
              <div className="flex gap-2">
                <Input value={part} onChange={(e) => setPart(e.target.value)} data-testid="oem-part-input"
                  placeholder="e.g. 21QVS18E00" className="h-9 text-[13px] font-mono"
                  onKeyDown={(e) => e.key === "Enter" && search()} />
                <Button onClick={search} disabled={busy} data-testid="oem-search-btn"
                  className="bg-indigo-600 hover:bg-indigo-700 shrink-0">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4 mr-1.5" /> Search OEM</>}
                </Button>
              </div>
            </div>
          </div>

          {/* Non-exact statuses */}
          {res && !exact && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 flex gap-2 text-[12px] text-amber-800" data-testid="oem-status-msg">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <div>{res.message || STATUS_MSG[res.match_status] || "No exact match."}</div>
            </div>
          )}

          {/* Exact match preview */}
          {exact && (
            <div className="space-y-3" data-testid="oem-preview">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2.5">
                <div className="flex items-center gap-2 text-[12px] font-semibold text-emerald-700">
                  <CheckCircle2 className="h-4 w-4" /> Exact product found
                </div>
                <div className="mt-1 text-[15px] font-semibold text-slate-900" data-testid="oem-product-name">{res.product_name}</div>
                <div className="mt-0.5 flex flex-wrap gap-1.5 items-center text-[11px] text-slate-500">
                  <span className="font-mono">{res.part_number}</span>
                  {res.region && <Badge variant="outline" className="text-[10px]">{res.region}</Badge>}
                  <Badge className="bg-indigo-600 text-[10px]">{res.source}</Badge>
                </div>
              </div>

              {/* Smart quotation description */}
              {res.smart_description && (
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Smart quotation description</div>
                  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12px] leading-relaxed text-slate-600 whitespace-pre-wrap" data-testid="oem-smart-desc">
                    {renderBold(res.smart_description)}
                  </div>
                </div>
              )}

              {/* Key specs quick grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid="oem-key-specs">
                {[["Processor", n.processor], ["Memory", n.memory], ["Storage", n.storage],
                  ["Graphics", n.gpu], ["Display", n.display_size], ["OS", n.operating_system],
                  ["Wireless", n.wireless], ["Battery", n.battery], ["Warranty", n.warranty]]
                  .filter(([, val]) => val).map(([l, val]) => (
                  <div key={l} className="rounded border border-slate-100 bg-slate-50 px-2 py-1">
                    <div className="text-[9px] uppercase tracking-wide text-slate-400">{l}</div>
                    <div className="text-[11px] text-slate-700 line-clamp-2">{val}</div>
                  </div>
                ))}
              </div>

              {/* Full specifications drawer */}
              <button onClick={() => setShowAll((s) => !s)} data-testid="oem-toggle-full"
                className="flex items-center gap-1 text-[12px] text-indigo-600 font-medium">
                <ChevronDown className={`h-4 w-4 transition-transform ${showAll ? "rotate-180" : ""}`} />
                {showAll ? "Hide" : "View"} complete specifications
              </button>
              {showAll && (
                <div className="space-y-2" data-testid="oem-full-specs">
                  {(res.categories || []).map((c) => (
                    <div key={c.category} className="rounded-lg border border-slate-200">
                      <div className="px-3 py-1.5 bg-slate-50 text-[11px] font-semibold text-slate-600 rounded-t-lg">{c.category}</div>
                      <div className="divide-y divide-slate-100">
                        {(c.items || []).map((it, i) => (
                          <div key={i} className="px-3 py-1.5 grid grid-cols-[130px_1fr] gap-2 text-[11px]">
                            <div className="text-slate-400">{it.label}</div>
                            <div className="text-slate-700 whitespace-pre-wrap">{it.value}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Product images */}
              {res.images?.length > 0 && (
                <div className="flex gap-2 overflow-x-auto pb-1" data-testid="oem-images">
                  {res.images.slice(0, 6).map((u, i) => (
                    <img key={i} src={u} alt="product" className="h-20 rounded border border-slate-200 object-contain bg-white shrink-0" />
                  ))}
                </div>
              )}

              {/* Datasheet / source links */}
              <div className="flex flex-wrap gap-2">
                {res.source_url && (
                  <a href={res.source_url} target="_blank" rel="noreferrer" data-testid="oem-open-source"
                    className="inline-flex items-center gap-1.5 text-[12px] px-2.5 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-700">
                    <ExternalLink className="h-3.5 w-3.5" /> Open in {res.source}
                  </a>
                )}
                {res.datasheet?.url && (
                  <a href={res.datasheet.url} target="_blank" rel="noreferrer" data-testid="oem-datasheet"
                    className="inline-flex items-center gap-1.5 text-[12px] px-2.5 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-700">
                    <FileText className="h-3.5 w-3.5" /> View datasheet
                  </a>
                )}
              </div>

              {/* Recommended services */}
              {res.services?.length > 0 && (
                <div data-testid="oem-services">
                  <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Recommended services (add to quote if SKU exists)</div>
                  {res.services.map((s, i) => (
                    <div key={i} className="text-[11px] text-slate-600 rounded border border-slate-100 bg-slate-50 px-2 py-1 mb-1">{s.name}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="px-5 py-3 border-t border-slate-100">
          <Button variant="outline" onClick={onClose} data-testid="oem-cancel">Cancel</Button>
          {exact && (
            <Button onClick={doImport} disabled={importing} data-testid="oem-import-btn"
              className="bg-emerald-600 hover:bg-emerald-700">
              {importing ? "Importing…" : "Import specifications"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
